import { useQuery, useQueryClient } from "@tanstack/react-query";

import { orgApiClient } from "@/libs/api";

// Query keys for consistent cache management
export const reviewsKeys = {
  all: ["reviews"] as const,
  lists: () => [...reviewsKeys.all, "list"] as const,
  list: (orgId: string) => [...reviewsKeys.lists(), orgId] as const
};

interface UseReviewsOptions {
  orgId: string;
  enabled?: boolean;
}

export function useReviews(options: UseReviewsOptions) {
  const { orgId, enabled = true } = options;

  return useQuery({
    queryKey: reviewsKeys.list(orgId),
    queryFn: async () => {
      return orgApiClient.getReviews(orgId);
    },
    enabled: enabled && !!orgId,
    staleTime: 10 * 60 * 1000 // 10 minutes for reviews
  });
}

// Utility hook for invalidating reviews cache
export function useReviewsInvalidation() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: reviewsKeys.all }),
    invalidateList: () => queryClient.invalidateQueries({ queryKey: reviewsKeys.lists() }),
    invalidateOrgReviews: (orgId: string) =>
      queryClient.invalidateQueries({ queryKey: reviewsKeys.list(orgId) })
  };
}
